import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  HStack,
  Progress,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { addSeconds } from "date-fns";
import { useTranslation } from "react-i18next";
import { FaCircleCheck, FaCircleExclamation } from "react-icons/fa6";
import { useNavigate } from "react-router";
import {
  StompSessionProvider,
  useStompClient,
  useSubscription,
} from "react-stomp-hooks";

import type { EmployeeStatus } from "@its4plan/authentication";

import { baseUrlFactory } from "~/lib/api/common";
import { useEmployeesStatus } from "~/lib/api/employees.api";
import { getLoginToken } from "~/lib/utils/local-storage.helpers";

export function CodePage() {
  const { t } = useTranslation();
  return (
    <StompProvider>
      <Container>
        <Button position="absolute" top={5} right={5}>
          {t("logout")}
        </Button>
        <Center>
          <RefreshedCode />
        </Center>
      </Container>
    </StompProvider>
  );
}

function StompProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();

  function onDisconnect() {
    void navigate("/login");
  }

  return (
    <StompSessionProvider
      url={`${baseUrlFactory("auth", import.meta.env.VITE_APP_AUTHENTICATION_URL as string)}/ws`}
      connectHeaders={{
        Authorization: `Bearer ${getLoginToken()}`,
      }}
      onDisconnect={onDisconnect}
    >
      {children}
    </StompSessionProvider>
  );
}

function RefreshedCode() {
  const { t } = useTranslation();
  const [authCode, setAutCode] = useState<AuthCode>();
  const client = useStompClient();
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const timeoutId = setInterval(() => {
      if (!authCode) {
        setError(true);
        return;
      } else {
        setError(false);
      }
      if (
        addSeconds(new Date(authCode.validUntil), 5).getTime() <
        new Date().getTime()
      ) {
        setAutCode(undefined);
        return;
      }
    }, 5000);
    return () => {
      clearInterval(timeoutId);
    };
  }, [authCode]);

  useSubscription(`/user/${getLoginToken()}/topic/auth-code`, (message) => {
    const event = message.body;
    if (event.startsWith("ERROR")) {
      console.error("Error receiving auth code: ", event);
      setError(true);
      return;
    }
    setAutCode(JSON.parse(message.body) as AuthCode);
  });
  useEffect(() => {
    client?.publish({ destination: "/app/topic/auth-code", body: "" });
  }, [client]);

  if (error) {
    return (
      <Flex direction="column" alignItems="center">
        <Text fontSize="2xl" color="red.500" mb={4}>
          {t("code.error")}
        </Text>
        <Button
          onClick={() => {
            window.location.reload();
          }}
        >
          {t("code.errorButton")}
        </Button>
      </Flex>
    );
  }
  return !authCode ? <Spinner /> : <NumericCode code={authCode} />;
}

type AuthCode = {
  code: number;
  validUntil: string;
  createdAt: string;
};

function NumericCode({ code }: { code: AuthCode }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  const { data: statuses = [] } = useEmployeesStatus();

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const started = new Date(code.createdAt).getTime();
      const validUntil = new Date(code.validUntil).getTime();
      const remainingTime = validUntil - now;
      const totalTime = validUntil - started;
      const progressValue = Math.max(0, (remainingTime / totalTime) * 100);
      setProgress(progressValue);
    }, 500);

    return () => clearInterval(interval);
  }, [code.validUntil, code.createdAt]);

  return (
    <Flex direction="column" alignItems="center">
      <Text fontSize="2xl" color="gray.500" mb={4}>
        {t("code.label")}
      </Text>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.100"
        borderRadius="md"
        boxShadow="md"
        p={4}
      >
        <Text fontSize="6xl" fontWeight="bold" color="teal.500">
          {code.code.toString().padStart(2, "0")}
        </Text>
      </Box>
      <Progress mt={5} mb={5} w="full" rounded="md" value={progress} />

      <SimpleGrid columns={2} spacing="40px">
        {statuses.map((status) => (
          <Box key={status.employee} borderWidth="1px" borderRadius="3px">
            <HStack>
              <Box lineHeight="10px" padding="10px">
                <StatusIcon
                  activeStatus={status.active}
                  pauseStatus={status.isInPause}
                />
              </Box>
              <Box padding="10px">{formatName(status)}</Box>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>
    </Flex>
  );
}

function formatName(employeeStatus: EmployeeStatus) {
  return `${employeeStatus.firstName} ${employeeStatus.lastName}`;
}

type StatusIconProps = {
  activeStatus: boolean;
  pauseStatus: boolean;
};

function StatusIcon({ activeStatus, pauseStatus }: StatusIconProps) {
  return (
    <>
      {activeStatus && !pauseStatus && <FaCircleCheck color="green" />}
      {!activeStatus && !pauseStatus && <FaCircleExclamation color={"red"} />}
      {activeStatus && pauseStatus && <FaCircleExclamation color={"yellow"} />}
    </>
  );
}
